import type { GoalRecord } from '~/models/goal'
import type { PhaseRecord } from '~/models/phase'
import { defaultProjectName, type ProjectRecord } from '~/models/project'
import type { WeeklyReviewRecord } from '~/models/weeklyReview'
import { type CalendarDate, type IsoTimestamp, toCalendarDate, unwrapDate } from '~/types/dates'
import type { ProjectId } from '~/types/ids'

/**
 * The schema v2 backfill, as a pure function (D-062, D-031).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  WHY THIS IS SHARED
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  Two things must bring data to version 2 and agree exactly: the database
 *  migration, which moves records inside a `versionchange` transaction, and the
 *  backup payload migration, which brings an older backup forward in memory
 *  before it is restored (D-031). Writing the rule twice would make the paired
 *  equivalence test a test of whether two people had the same idea.
 *
 *  So the *decision* lives here, once, as a pure function over plain records,
 *  and each side does only its own plumbing — cursor writes on one, array
 *  mapping on the other. The paired test then checks the plumbing, which is
 *  where the two can actually diverge.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  WHAT IT DECIDES
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  **One project, covering everything that already exists.** Before version 2
 *  the app held a single line of phase history with no way to express a break
 *  between one undertaking and the next, so every existing phase, goal and
 *  weekly review belongs to the one implicit project the user has been running.
 *  Claiming otherwise would mean inventing a boundary they never drew.
 *
 *  **The project is left `active`, whatever state the phases are in.** A
 *  finished phase is not a finished project: the user ended a phase, and was
 *  never offered the choice to end a project because projects did not exist.
 *  Marking it `completed` would record a decision they never made, which is
 *  exactly what D-020 forbids.
 *
 *  **No recommendation snapshot.** This project predates the route engine, so
 *  nothing recommended it. The field stays absent rather than being filled with
 *  a reconstruction, which would attribute advice to Project Sola that it never
 *  gave.
 *
 *  **No clock, and no random identifier.** Every value is derived from the
 *  records themselves, which is what lets the two migrations produce byte-equal
 *  results from equal input and makes the paired test a plain comparison.
 */

/** The version this backfill produces. A literal, not `DB_SCHEMA_VERSION`:
    a released migration must keep stamping 2 after the constant moves on. */
export const PROJECTS_SCHEMA_VERSION = 2

export interface BackfillInput {
  readonly phases: readonly PhaseRecord[]
  readonly goals: readonly GoalRecord[]
  readonly weeklyReviews: readonly WeeklyReviewRecord[]
}

/** Only the records that change. An empty list means nothing to write. */
export interface BackfillPlan {
  readonly project: ProjectRecord
  readonly phases: readonly PhaseRecord[]
  readonly goals: readonly GoalRecord[]
  readonly weeklyReviews: readonly WeeklyReviewRecord[]
}

/**
 * Plan the backfill, or `undefined` when there is nothing to carry forward.
 *
 * A fresh install reaches version 2 with no records at all, and inventing an
 * empty project for it would put a nameless thing in the user's history before
 * they had opened the app.
 */
export function planProjectBackfill(input: BackfillInput): BackfillPlan | undefined {
  const phases = [...input.phases].sort(byDateThenId(phaseStart))
  const goals = [...input.goals].sort(byDateThenId(goalStart))
  const reviews = [...input.weeklyReviews].sort(byDateThenId(reviewStart))

  const anchor = phases[0]?.id ?? goals[0]?.id ?? reviews[0]?.id
  if (anchor === undefined) return undefined

  const startDate = earliest([
    ...phases.map(phaseStart),
    ...goals.map(goalStart),
    ...reviews.map(reviewStart),
  ])
  const targetDate = chooseTargetDate(phases, goals, reviews, startDate)

  /* The project has existed for as long as its oldest record. Taking the
     earliest `createdAt` keeps this deterministic and truthful; reading a clock
     here would stamp it with the moment the user happened to install an
     update. */
  const createdAt = earliestTimestamp([...phases, ...goals, ...reviews])

  const project: ProjectRecord = {
    /* Derived, not random. Two runs of this migration over the same data — the
       database one and the payload one — must produce the same identifier, or
       a restored backup would reference a project that does not exist. */
    id: `backfill-${anchor}` as ProjectId,
    name: defaultProjectName(targetDate),
    status: 'active',
    startDate,
    targetDate,
    createdAt,
    updatedAt: createdAt,
    schemaVersion: PROJECTS_SCHEMA_VERSION,
  }

  return {
    project,
    phases: phases.map((phase) => stamp(phase, project.id)),
    goals: goals.map((goal) => stamp(goal, project.id)),
    weeklyReviews: reviews.map((review) => stamp(review, project.id)),
  }
}

/**
 * Stamp the foreign key without touching anything else.
 *
 * `updatedAt` deliberately does not move: the user did not change this record,
 * and a history screen that showed every phase as edited on upgrade day would
 * be reporting something that never happened. `schemaVersion` does move,
 * because that field means "the database version this record was last written
 * against" and it now genuinely is 2.
 */
function stamp<T extends { readonly schemaVersion: number }>(record: T, projectId: ProjectId): T {
  return { ...record, projectId, schemaVersion: PROJECTS_SCHEMA_VERSION }
}

/**
 * The date the project is aimed at.
 *
 * An active goal's target date is the user's own statement of when they want to
 * be done, so it wins. Failing that, take the furthest horizon already on
 * record rather than inventing one — a project that claims to end before its
 * own phases do would trip the D-020 end warning the moment it was created.
 */
function chooseTargetDate(
  phases: readonly PhaseRecord[],
  goals: readonly GoalRecord[],
  reviews: readonly WeeklyReviewRecord[],
  fallback: CalendarDate,
): CalendarDate {
  const activeGoal = goals.filter((goal) => goal.status === 'active')
  const candidates = [
    ...(activeGoal.length > 0 ? activeGoal : goals).map((goal) => goal.targetDate),
    ...phases.map((phase) => phase.actualEndDate ?? phase.plannedEndDate),
    ...reviews.map(reviewStart),
  ]
  return candidates.length === 0 ? fallback : latest(candidates)
}

function phaseStart(phase: PhaseRecord): CalendarDate {
  return phase.startDate
}

function goalStart(goal: GoalRecord): CalendarDate {
  return goal.startDate
}

function reviewStart(review: WeeklyReviewRecord): CalendarDate {
  return review.weekStartDate
}

/**
 * Order by date, breaking ties on the identifier.
 *
 * The tie-break is not cosmetic: `getAll` order is not something to rely on for
 * choosing an anchor identifier, and two records sharing a date must not make
 * the generated project id depend on which one IndexedDB handed back first.
 */
function byDateThenId<T extends { readonly id: string }>(
  dateOf: (record: T) => CalendarDate,
): (left: T, right: T) => number {
  return (left, right) => {
    // Lexical comparison is exact for zero-padded YYYY-MM-DD.
    const byDate = unwrapDate(dateOf(left)).localeCompare(unwrapDate(dateOf(right)))
    return byDate !== 0 ? byDate : left.id.localeCompare(right.id)
  }
}

function earliest(dates: readonly CalendarDate[]): CalendarDate {
  const values = dates.map(unwrapDate).sort()
  const first = values[0]
  /* Callers only reach this with at least one record, and the value came from a
     `CalendarDate` in the first place, so the conversion cannot fail. */
  return toCalendarDate(first ?? '') as CalendarDate
}

function latest(dates: readonly CalendarDate[]): CalendarDate {
  const values = dates.map(unwrapDate).sort()
  return toCalendarDate(values[values.length - 1] ?? '') as CalendarDate
}

function earliestTimestamp(records: readonly { readonly createdAt: IsoTimestamp }[]): IsoTimestamp {
  /* ISO-8601 UTC timestamps sort lexically. Callers only reach this with at
     least one record. */
  return [...records.map((record) => record.createdAt)].sort()[0] as IsoTimestamp
}
